import { PartialBy } from './typeUtils';
import { ScrubbedUser } from './user';

export interface UserActionRecord {
  _id: string;
  createdAt: string;
  userId: string;
  url: string;
  method: string;
  body: any;
  query: { [key: string]: string };
  action: UserAction;
  property?: string;
  urlParams: { [key: string]: string };
  additionalData?: {
    _id: string;
    name: string;
  };
}

export type LogEntity = {
  _id: string;
  name: string;
};
export type ParamToLogEntity = { [key: string]: Array<LogEntity> };
export type LogSummary = { [key: string]: LogEntity };

export type UserActionRecordDetailed = PartialBy<UserActionRecord, 'userId'> & {
  user: ScrubbedUser;
  summary?: LogSummary;
};

export type AvailableFiltersOptions = {
  users: ScrubbedUser[];
};

export type UserActionsMixedResponse = {
  records: UserActionRecordDetailed[];
  pagination: UserActionRecordPagination;
  availableFilters: AvailableFiltersOptions;
};
// Execute this snippet to generate all the available actions for the UserAction type.
/**
 *
 const result = [];
 const entities = [
 "'PLAN",
 "'EVENT",
 "'EVENT TEMPLATE",
 "'SAILING",
 "'SAILING TEMPLATE",
 "'VENUE",
 "'VENUE TEMPLATE",
 "'STAFF",
 "'STAFF TEMPLATE",
 "'SAILING TEMPLATE",
 "'GROUP EVENT",
 "'GROUP BOOKING",
 "'ITINERARY",
 "'CONTENT TEMPLATE",
 "'FEED"
 ];
 entities.forEach(ent => {
    ["DELETED'", "CREATED'", "UPDATED'"].forEach(act => {
      result.push(ent + ' ' + act);
    });
  });

 console.log(result.join(" | "))
 *
 */

export const UserActions = [
  'SILENT',
  'PLAN DELETED',
  'PLAN CREATED',
  'PLAN UPDATED',
  'EVENT DELETED',
  'EVENT CREATED',
  'EVENT UPDATED',
  'EVENT TEMPLATE DELETED',
  'EVENT TEMPLATE CREATED',
  'EVENT TEMPLATE UPDATED',
  'IMAGE UPDATED',
  'IMAGE DELETED',
  'SAILING DELETED',
  'SAILING CREATED',
  'SAILING UPDATED',
  'SAILING TEMPLATE DELETED',
  'SAILING TEMPLATE CREATED',
  'SAILING TEMPLATE UPDATED',
  'VENUE DELETED',
  'VENUE CREATED',
  'VENUE UPDATED',
  'VENUE TEMPLATE DELETED',
  'VENUE TEMPLATE CREATED',
  'VENUE TEMPLATE UPDATED',
  'STAFF DELETED',
  'STAFF CREATED',
  'STAFF UPDATED',
  'STAFF TEMPLATE DELETED',
  'STAFF TEMPLATE CREATED',
  'STAFF TEMPLATE UPDATED',
  'GROUP EVENT DELETED',
  'GROUP EVENT CREATED',
  'GROUP EVENT UPDATED',
  'GROUP BOOKING DELETED',
  'GROUP BOOKING CREATED',
  'GROUP BOOKING UPDATED',
  'GROUP RESOURCE CREATED',
  'GROUP RESOURCE ARCHIVED',
  'GROUP RESOURCE UPDATED',
  'ITINERARY DELETED',
  'ITINERARY CREATED',
  'ITINERARY UPDATED',
  'ITINERARY PUBLISHED',
  'CONTENT TEMPLATE DELETED',
  'CONTENT TEMPLATE CREATED',
  'CONTENT TEMPLATE UPDATED',
  'FEED DELETED',
  'FEED CREATED',
  'FEED UPDATED',
  'PROPERTY CREATED',
  'PROPERTY UPDATED',
  'PROPERTY DELETED',
  'PROPERTY TAGS UPDATED',
  'PROPERTY CONFIGS UPDATED',
  'BRAND VENUE TYPES UPDATED',
  'BRAND UPDATED',
  'BRAND MAPPING UPDATED',
  'DAY TEMPLATE UPDATED',
  'DAY TEMPLATE TRANSFERED',
  'DAY TEMPLATE EVENT CREATED',
  'DAY TEMPLATE EVENT UPDATED',
  'DAY TEMPLATE EVENT DELETED',
  'DAY TEMPLATE VENUE DATA UPDATED',
  'DAY TEMPLATE DELETED',
  'TEMPLATE FRAGMENTS LINKED',
  'TEMPLATE FRAGMENTS UNLINKED',
  'LIBRARY TRANSLATIONS UPDATED',
  'UI TRANSLATIONS UPDATED',
  'USER CREATED',
  'USER UPDATED',
  'USER DELETED',
  'IMAGE UPLOADED',
  'IMAGE VARIANT UPLOADED',
  'RESERVATIONS CREATED',
  'RESERVATIONS UPDATED',
  'RES SCHEDULE UPDATED',
  'RES SCHEDULE DELETED',
];

export type UserAction = (typeof UserActions)[number];

export interface RecordConfig {
  action: UserAction;
}

export const FilterActionTypes = [
  'CREATED',
  'DELETED',
  'UPDATED',
  'ALL',
  'LINKED',
  'UNLINKED',
] as const;
export type FilterActionType = (typeof FilterActionTypes)[number];

export const GroupActionTypes = [
  ...new Set(
    UserActions.map(userAction => {
      let groupAction = userAction;

      FilterActionTypes.forEach(actionType => {
        if (groupAction.includes(actionType)) {
          groupAction = groupAction.replace(` ${actionType}`, '');
        }
      });

      return groupAction;
    })
  ),
];
export type GroupActionType = (typeof GroupActionTypes)[number];

export interface UserActionRecordFilters {
  users?: Array<string>;
  from?: string;
  to?: string;
  action?: FilterActionType;
  subAction?: UserAction;
  groupAction?: GroupActionType;
  page?: number;
  propertyId?: string;
  venueTemplateId?: Array<string>;
  eventTemplateId?: Array<string>;
  contentTemplateId?: Array<string>;
  staffTemplateId?: Array<string>;
  templateId?: Array<string>;
  refersId?: Array<string>;
  groupBookingId?: Array<string>;
  groupBookingPartyId?: Array<string>;
  archived?: boolean | undefined;
  locked?: boolean | undefined;
  sort?: Sorting;
}

export interface UserActionRecordPagination {
  count: number;
  pages: number;
  recordsPerPage: number;
}

export enum SortOrder {
  ASC = 1,
  DESC = -1,
}

export type Sorting = {
  [key: string]: SortOrder;
};

export type SummaryKey =
  | 'propertyId'
  | 'brandId'
  | 'sailingId'
  | 'planId'
  | 'venueTemplateId'
  | 'templateId'
  | 'groupBookingId'
  | 'sailingDayId'
  | 'eventTemplateId'
  | 'contentTemplateId'
  | 'eventId';

type PickDeletedEventSummaryKey<T extends SummaryKey> = T extends 'eventId' | 'venueTemplateId'
  ? {
      _id: string;
      name: string;
    }
  : never;

export type DeletedEventAdditionalData = {
  [K in 'eventId' | 'venueTemplateId']?: PickDeletedEventSummaryKey<K>;
};

// You could extend the UserLogsAdditionalData by using union type
// Every new type should have a SummaryKey as key and _id and name
// to work properly with the log summaries
export type UserLogsAdditionalData = DeletedEventAdditionalData;

export const getIdByBodyKey = (
  key: string,
  action: UserAction,
  body: any
): SummaryKey | undefined => {
  const bodyIds: { [key: string]: SummaryKey } = {
    property: 'propertyId',
    brand: 'brandId',
    sailing: 'sailingId',
    plan: 'planId',
    venue: 'venueTemplateId',
    templateId: 'templateId',
    group: 'groupBookingId',
    sailingDayId: 'sailingDayId',
  };

  if (bodyIds[key]) return bodyIds[key];

  let value: SummaryKey | undefined;
  if (key === 'template') {
    if (['VENUE UPDATED', 'VENUE CREATED', 'DAY TEMPLATE VENUE DATA UPDATED'].includes(action)) {
      value = 'venueTemplateId';
    } else if (
      [
        'EVENT UPDATED',
        'EVENT CREATED',
        'DAY EVENT TEMPLATE CREATED',
        'DAY EVENT TEMPLATE UPDATED',
        'DAY TEMPLATE EVENT CREATED',
      ].includes(action)
    ) {
      value = 'eventTemplateId';
    }
  } else if (key === 'refers') {
    if (body.type === 'contenttemplate') {
      value = 'contentTemplateId';
    } else if (body.type === 'venuetemplate') {
      value = 'venueTemplateId';
    } else if (body.type === 'eventtemplate') {
      value = 'eventTemplateId';
    }
  }

  return value;
};
